import * as videojs from 'video.js';
import * as utils from '../utils/utils.js';
import * as userPerms from '../utils/userPerms.js';
import { langMap } from '../utils/languages.js';
import { VIEWING_STATE } from '../events/events.config.js';
import { TableClient, AzureSASCredential } from '@azure/data-tables';
import { differenceInMinutes, addMinutes, addSeconds, isBefore } from 'date-fns';
import { getDateFromUTCString } from '../utils/utils.js';
require('videojs-hls-quality-selector');

const videoJS = videojs.default;

export default class WatchService {
    constructor($rootScope, $log, $interval, uiService) {
        this.CURRENT_VIEW = Object.freeze({
            undef: 0,
            countdown: 1,
            video: 2,
            ended: 3,
        });

        this.$rootScope = $rootScope;
        this.$log = $log;
        this.$interval = $interval;
        this.userInteractionService = uiService;
        this.event = null;
        this.currentView = this.CURRENT_VIEW.undef;
        this.user = utils.getCurrentUser();
        this.videoSwitchedFromCountdown = false;
        this.useCloudBackupUrl = false;
        this.forceVideoView = false;
        this.isVideoErrorState = false;
        this.hasPlaybackStarted = false;
        this.useOnPremStreaming = false;

        const urlParams = new URLSearchParams(window.location.search);
        let customStreamSourceParam = urlParams.get('ss');
        this.customStreamSource = customStreamSourceParam ? customStreamSourceParam : null;
        this.selectedOnPremPlaylistUrl = undefined;

        this.enableStatistics = siteSettings.statisticsSettings.filter((item) => item.key === 'EnableStatistics')[0]?.value === 'True';
        const account = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'StorageAccount'
        )[0]?.value;
        const sas = siteSettings.statisticsSettings.filter((item) => item.key === 'SAS')[0]?.value;
        const statsTableName = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'StatsTableName'
        )[0]?.value;
        const liveViewsTableName = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'LiveViewsTableName'
        )[0]?.value;
        const vodViewsTableName = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'VodViewsTableName'
        )[0]?.value;

        this.statsTableClient = new TableClient(
            `https://${account}.table.core.windows.net`,
            statsTableName,
            new AzureSASCredential(sas)
        );
        this.liveViewsTableClient = new TableClient(
            `https://${account}.table.core.windows.net`,
            liveViewsTableName,
            new AzureSASCredential(sas)
        );
        this.vodViewsTableClient = new TableClient(
            `https://${account}.table.core.windows.net`,
            vodViewsTableName,
            new AzureSASCredential(sas)
        );

        const randomizeViewTicksSeconds = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'RandomizeViewTicksSeconds'
        )[0]?.value;
        const viewTicksSecondsOffset = parseInt(
            Math.floor(Math.random() * randomizeViewTicksSeconds)
        );
        const now = new Date();
        this.nextTickNotBeforeCountdown = addSeconds(now, viewTicksSecondsOffset);
        this.nextTickNotBeforeVideo = addSeconds(now, viewTicksSecondsOffset);

        // Register video views for the statistics
        if (this.enableStatistics) {
          this.$interval(() => {
              Promise.resolve(this.registerView());
          }, 2000);
        }

        // Periodically save the current video time index for VOD
        this.$interval(() => {
          if (this.isVideoPublished()) {
              let videoElem = utils.getVideoElem();
              if (
                  videoElem &&
                  !videoElem.paused &&
                  !videoElem.ended &&
                  this.videoPlayerState === 'playing'
              ) {
                  localStorage.setItem(utils.vodCurrentTimeKey(this.event), videoElem.currentTime);
              }
          }
        }, 10000);
    }

    async registerView() {
        if (!this.event)
            return;

        var vjsPlayer = videoJS('vjs-player');
        const viewTimeTicksMinutes = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'ViewTimeTicksMinutes'
        )[0]?.value;
        const maxTableProps = siteSettings.statisticsSettings.filter(
            (item) => item.key === 'MaxTableProps'
        )[0]?.value;

        const isCountdown = this.event.viewingState === 0;
        const isVod = this.isVideoPublished();
        const isEmbed = siteSettings.isEmbeddedPage;

        // Log the stats entry for the new user
        let updateStatsUser = false;
        const userStorageKey = this.event.accessKey + '-statsUserID';
        let statsUserId = localStorage.getItem(userStorageKey);
        if (!statsUserId) {
            statsUserId = crypto.randomUUID();
            updateStatsUser = true;
        }

        const userLocalIPKey = this.event.accessKey + '-statsUserIP';
        let localIpAddress = localStorage.getItem(userLocalIPKey);
        let officeSite = null;
        let isVpn = false;
        let isOfficeSite = false;
        if ((!localIpAddress || localIpAddress === 'null') && this.networkInfo && this.networkInfo.clientIpAddress) {
            localIpAddress = this.networkInfo.clientIpAddress;
            officeSite = this.networkInfo.location;
            isVpn = this.networkInfo.isOfficeSite === false && this.networkInfo.location.indexOf('RAS') > -1;
            isOfficeSite = this.networkInfo.isOfficeSite === true;
            updateStatsUser = true;
        }

        if (updateStatsUser) {
            utils.log(
                'statistics',
                'Upserting a stats entry for the current user',
                true,
                1
            );

            let userId = statsUserId;
            let isOrgUser = false;
            if (this.user && this.user._au) {
                userId = utils.getHashCode(this.user._au);
                isOrgUser = userPerms.aadAuthenticated(this.user);
            }

            const statsEntity = {
                partitionKey: this.event.accessKey,
                rowKey: statsUserId,
                userAgent: window.navigator.userAgent,
                country: localStorage.getItem('vcl-country'),
                city: localStorage.getItem('vcl-city'),
                region: localStorage.getItem('vcl-region'),
                georegion: localStorage.getItem('vcl-georegion'),
                continent: localStorage.getItem('vcl-continent'),
                userId: userId,
                isOrgUser: isOrgUser,
                localIpAddress: localIpAddress,
                officeSite: officeSite,
                isOfficeSite: isOfficeSite,
                isVpn: isVpn,
            };
            utils.log('statistics', JSON.stringify(statsEntity), true, 1);

            try {
                await this.statsTableClient.upsertEntity(statsEntity, 'Merge');
                localStorage.setItem(userStorageKey, statsUserId);
                localStorage.setItem(userLocalIPKey, localIpAddress);
            } catch (error) {
                utils.log('statistics', JSON.stringify(error), false, 3);
            }
        }

        // For countdown or live video, wait until the randomized offset time is reached,
        // in order to spread out the API calls to Azure Storage and avoid hitting the threshold
        const now = new Date();
        if (isCountdown && isBefore(now, this.nextTickNotBeforeCountdown)) {
            return;
        } else if (!isCountdown && !isVod && isBefore(now, this.nextTickNotBeforeVideo)) {
            return;
        }

        // Create an entity to track views over time
        const viewEntity = {
            partitionKey: this.event.accessKey,
            rowKey: statsUserId,
            isEmbed: isEmbed,
        };
        let ticksStorageKey = this.event.accessKey;

        if (isCountdown) {
            // For the countdown page, log the same tick at the defined interval
            ticksStorageKey += '-lastCountdownTickTime';
            let minutesSinceLastTick = -1;
            let lastTickLoggedTime = localStorage.getItem(ticksStorageKey);
            if (lastTickLoggedTime) {
                minutesSinceLastTick = differenceInMinutes(
                    new Date(),
                    getDateFromUTCString(lastTickLoggedTime)
                );
            }
            if (minutesSinceLastTick === -1 || minutesSinceLastTick >= viewTimeTicksMinutes) {
                utils.log(
                    'statistics',
                    '[COUNTDOWN] Last tick was logged at ' +
                        lastTickLoggedTime +
                        '. Logging a new countdown tick.',
                    true,
                    1
                );
                viewEntity['countdown'] = 1;
                viewEntity['isVideoView'] = 0;
                utils.log('statistics', JSON.stringify(viewEntity), true, 1);

                try {
                    await this.liveViewsTableClient.upsertEntity(viewEntity, 'Merge');
                    localStorage.setItem(ticksStorageKey, new Date().toISOString());
                    this.nextTickNotBeforeCountdown = addMinutes(new Date(), viewTimeTicksMinutes);
                } catch (error) {
                    utils.log('statistics', JSON.stringify(error), false, 3);
                }
            }
        } else if (this.videoPlayerState === 'playing') {
            let ticks = -1;
            let lastTickLogged = null;
            viewEntity['isVideoView'] = 1;

            if (!isVod) {
                // For live, check the time since the event start time
                const minutesSinceStart = differenceInMinutes(
                    new Date(),
                    utils.getDateFromUTCString(this.event.startTime)
                );
                ticks = minutesSinceStart - (minutesSinceStart % viewTimeTicksMinutes);
                ticksStorageKey += '-lastLiveTick';
            } else {
                // For VOD, check the time played in the video player
                const minutesSinceStart = vjsPlayer.currentTime() / 60;
                ticks = minutesSinceStart - (minutesSinceStart % viewTimeTicksMinutes);
                ticksStorageKey += '-lastVodTick';
            }
            lastTickLogged = parseInt(localStorage.getItem(ticksStorageKey));

            if (ticks > 0 && ticks / viewTimeTicksMinutes >= maxTableProps) {
                utils.log(
                    'statistics',
                    "Maximum number of table properties reached, can't log any more view statistics",
                    false,
                    2
                );
            } else if (ticks > -1 && (lastTickLogged === null || ticks !== lastTickLogged)) {
                const logPrefix = isVod ? '[VOD]' : '[LIVE]';
                utils.log(
                    'statistics',
                    logPrefix +
                        ' Current tick is ' +
                        ticks +
                        ', last tick logged is ' +
                        lastTickLogged +
                        '. Logging a new view entity.',
                    true,
                    1
                );
                const ticksPropName =
                    'min' + (ticks < 10 ? '0' + ticks.toString() : ticks.toString());
                viewEntity[ticksPropName] = 1;
                utils.log('statistics', JSON.stringify(viewEntity), true, 1);

                const tableClient = !isVod ? this.liveViewsTableClient : this.vodViewsTableClient;
                try {
                    await tableClient.upsertEntity(viewEntity, 'Merge');
                    localStorage.setItem(ticksStorageKey, ticks);
                    if (!isVod)
                        this.nextTickNotBeforeVideo = addMinutes(new Date(), viewTimeTicksMinutes);
                } catch (error) {
                    utils.log('statistics', JSON.stringify(error), false, 3);
                }
            }
        }
    }

    isVideoPublished(srv) {
      if (!srv)
        srv = this;
      return srv.event.postEventBehavior === 1 &&
        ((!srv.event.useWowzaVideo && srv.event.viewingState === VIEWING_STATE.published) ||
        (srv.event.useWowzaVideo && !srv.event.wvStreamIsLive && srv.event.hlsPublicVodUrl));
    }

    videoIsVisible() {
        return this.currentView === this.CURRENT_VIEW.video;
    }

    countdownIsVisible() {
        return this.currentView === this.CURRENT_VIEW.countdown;
    }

    endedInfoIsVisible() {
        return this.currentView === this.CURRENT_VIEW.ended;
    }

    configurePlayerEvents() {
        let srv = this;

        utils.log('videoplayer', 'Setting up video player for the first time', true, 1);

        var vjsPlayer = videoJS('vjs-player');

        if (!siteSettings.isMobileDevice) {
          vjsPlayer.hlsQualitySelector();
        }

        vjsPlayer.on('ready', () => {
            this.videoPlayerState = 'ready';
            utils.log('videoplayer', 'Ready state', true, 1);

            // Add text tracks
            if (this.isVideoPublished(srv)) {
                if (srv.event.voDChaptersFilePath) {
                    vjsPlayer.addRemoteTextTrack(
                        {
                            kind: 'chapters',
                            label: 'Chapters',
                            language: 'en',
                            id: 'vjsChapters',
                            src: srv.event.voDChaptersFilePath,
                        },
                        true
                    );
                }

                if (srv.event.voDSubsFilePath) {
                    // Split the URLs and extract the languages
                    const subUrls = srv.event.voDSubsFilePath.split(';');
                    for (let i = 0; i < subUrls.length; i++) {
                        const subUrl = subUrls[i];
                        const langMatch = subUrl.match(/_([a-z]{2,2})\.vtt/i);
                        if (langMatch && langMatch.length > 1) {
                            const lang = langMatch[1];
                            const langNames = langMap[lang];
                            let langName = lang;
                            if (langNames !== undefined && langNames !== null) {
                                langName = langNames[0];
                            }
                            vjsPlayer.addRemoteTextTrack(
                                {
                                    kind: 'subtitles',
                                    label: langName,
                                    language: lang,
                                    id: 'vjsSubs-' + lang,
                                    src: subUrl,
                                },
                                true
                            );
                        }
                    }
                }
            }
        });

        vjsPlayer.on('error', () => {
          this.videoPlayerState = 'error';
          this.isVideoErrorState = true;
        });

        vjsPlayer.on('playing', () => {
          this.videoPlayerState = 'playing';
          this.hasPlaybackStarted = true;
          this.isVideoErrorState = false;
          utils.log('videoplayer', 'Playing state', true, 1);
        });

        vjsPlayer.on('ended', () => {
          this.videoPlayerState = 'ended';
            utils.log('videoplayer', 'Ended state', true, 1);

            // Reset stored play time index
            localStorage.removeItem(utils.vodCurrentTimeKey(this.event));
        });

        vjsPlayer.on('pause', () => {
          this.videoPlayerState = 'paused';
          utils.log('videoplayer', 'Pause state', true, 1);
        });

        vjsPlayer.on('waiting', () => {
          this.videoPlayerState = 'waiting';
            utils.log('videoplayer', 'Waiting state', true, 1);
        });
    }

    getPlaylistUrl() {
        let playlistUrl = '';

        // If webcast uses Wowza Video and is live
        if (this.event.useWowzaVideo && this.event.wvStreamIsLive) {
          if (
            this.event.useLocalOffloading &&
            (this.useOnPremStreaming ||
            (!this.useOnPremStreaming &&
                this.customStreamSource &&
                this.customStreamSource !== 'cloud' &&
                this.customStreamSource !== 'chinacdn')
            )) {
              // If user is on prem
              if (this.selectedOnPremPlaylistUrl) {
                playlistUrl = this.selectedOnPremPlaylistUrl;
              } else {
                  // let serverUrls =
                  //     this.onPremStreamSources[
                  //         this.customStreamSource
                  //             ? this.customStreamSource
                  //             : this.networkInfo.streamSource
                  //     ];
                  // let rnd = Math.floor(Math.random() * serverUrls.length);
                  // const fullHLSUrl = new URL(this.event.hlsPublicLiveUrl);
                  // playlistUrl = serverUrls[rnd] + fullHLSUrl.pathname;
                  // this.selectedOnPremPlaylistUrl = playlistUrl;

                  // HACK: The code commented out above uses local servers with Wowza Video as origin.
                  // Since the caching doesn't work as expected though, we are for now falling back to the old
                  // solution of using WSE on prem as origin. A manual stream target from WSE to WV has to be set up
                  // for this to work.

                  const streamSource = this.customStreamSource
                    ? this.customStreamSource
                    : this.networkInfo.streamSource;

                  let playlistUrls =
                        this.onPremStreamUrls[streamSource];
                  if (streamSource === 'got' && playlistUrls && playlistUrls.length > 0) {
                    let rnd = Math.floor(Math.random() * playlistUrls.length);
                    playlistUrl = playlistUrls[rnd];
                    this.selectedOnPremPlaylistUrl = playlistUrl;
                  } else {
                    playlistUrl = this.event.hlsPublicLiveUrl;
                  }
              }
            } else if (this.event.hlsPublicLiveUrl)
            {
              // If user is off-site
              playlistUrl = this.event.hlsPublicLiveUrl;
            }
        }
        // If event uses Wowza Video and has a VOD recording
        else if (this.event.useWowzaVideo && this.event.hlsPublicVodUrl) {
          playlistUrl = this.event.hlsPublicVodUrl;
        }
        // If the recorded version is published, provide that playlist regardless of time.
        // Otherwise, provide the live URL.
        else if (this.event.viewingState === VIEWING_STATE.published && this.voDStreamUrl) {
            if (
                this.useOnPremStreaming ||
                (!this.useOnPremStreaming &&
                    this.customStreamSource &&
                    this.customStreamSource !== 'cloud' &&
                    this.customStreamSource !== 'chinacdn' &&
                    this.customStreamSource !== 'backup')
            ) {
                let playlistUrls =
                    this.onPremVodUrls[
                        this.customStreamSource
                            ? this.customStreamSource
                            : this.networkInfo.streamSource
                    ];
                let rnd = Math.floor(Math.random() * playlistUrls.length);
                playlistUrl = playlistUrls[rnd];
            } else {
                playlistUrl = utils.isUserInChina() ? this.voDStreamUrlChina : this.voDStreamUrl;
            }
        }
        // If a custom server location has been clicked, stream from on-prem even if cloud streaming
        // has been selected automatically by the application
        else if (
            this.useOnPremStreaming ||
            (!this.useOnPremStreaming &&
                this.customStreamSource &&
                this.customStreamSource !== 'cloud' &&
                this.customStreamSource !== 'chinacdn')
        ) {
            if (this.customStreamSource === 'backup') {
                playlistUrl = this.cloudStreamBackupUrl;
            } else {
                if (this.selectedOnPremPlaylistUrl) {
                    playlistUrl = this.selectedOnPremPlaylistUrl;
                } else {
                    let playlistUrls =
                        this.onPremStreamUrls[
                            this.customStreamSource
                                ? this.customStreamSource
                                : this.networkInfo.streamSource
                        ];
                    let rnd = Math.floor(Math.random() * playlistUrls.length);
                    playlistUrl = playlistUrls[rnd];
                    this.selectedOnPremPlaylistUrl = playlistUrl;
                }
            }
        } else {
            if (this.useCloudBackupUrl && this.cloudStreamBackupUrl !== '') {
                // Don't use backup stream in China
                if (utils.isUserInChina()) {
                    playlistUrl = this.chinaCdnStreamUrl;
                } else {
                    playlistUrl = this.cloudStreamBackupUrl;
                }
            } else {
                playlistUrl =
                    (utils.isUserInChina() && !this.customStreamSource) ||
                    this.customStreamSource === 'chinacdn'
                        ? this.chinaCdnStreamUrl
                        : this.cloudStreamUrl;
            }
        }

        return playlistUrl;
    }

    selectUserLocation() {
      utils.log('watch', 'Determining the geographical location of the current user (on-premise or external)', true, 1);
      let srv = this;
      return srv.getNetworkInfo().then(
          (data) => {
              utils.log('broadcast', JSON.stringify(data), false, 1);

              // Data received from network info web service, which means the user is connected to the corporate LAN
              // Save the network info
              srv.networkInfo = data;

              srv.useOnPremStreaming =
                  srv.networkInfo.streamSource !== 'cloud' &&
                  !srv.networkInfo.useCloudStreamSource;
          },
          (err) => {
              utils.log(
                  'broadcast',
                  'Error received from network info web service - user is probably outside of the corporate LAN',
                  true,
                  3,
                  err
              );
              srv.useOnPremStreaming = false;
          }
      );
  }

    getNetworkInfo() {
        const { playlistSvcUrl } = siteSettings;

        // If the forceOnPremStreaming variable is used, return default stream server values even if the user
        // is outside the corporate LAN
        if (siteSettings.forceOnPremStreaming) {
            return Promise.resolve({
                country: 'se',
                location: 'Gothenburg',
                subnet: '10.246.0.0/16',
                streamSource: 'got',
                streamSourceName: 'Gothenburg',
                isOfficeSite: true,
                useCloudStreamSource: false,
                clientIpAddress: '127.0.0.1',
                streamServerUrl: 'https://got5.vcl.volvocars.net:443/',
                isSourceServer: true,
                sourcePlaylistUrl:
                    'https://got5.vcl.volvocars.net:443/' +
                    'live' +
                    '/ngrp:' +
                    this.event.accessKey +
                    '_all/playlist.m3u8',
            });
        } else {
            // If the forceCloudStreaming variable string is used, return an error
            if (siteSettings.isMobileDevice || siteSettings.forceCloudStreaming) {
                return Promise.reject();
            } else {
                //Make the call to the network info web service
                let userParams =
                    siteSettings.enableAccessLog && this.user !== null
                        ? '&userId=' +
                          encodeURIComponent(this.user._ik) +
                          '&userName=' +
                          encodeURIComponent(this.user._au)
                        : '';
                let url =
                    playlistSvcUrl +
                    '/api/stream/getnetworkinfo' +
                    '?appname=live' +
                    '&streamname=' +
                    this.event.accessKey +
                    userParams;

                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 2000);

                return fetch(url, {
                    credentials: 'omit',
                    signal: controller.signal,
                })
                    .then((response) => {
                        clearTimeout(timeoutId);
                        return response.json();
                    })
                    .then((data) => {
                        return data;
                    });
            }
        }
    }

    prepareVideoView() {
        if (this.event.videoEmbedCode && this.event.videoEmbedCode.length > 0)
            return;
        else {
          utils.log('watch', 'Preparing the video player for stream playback', true, 1);
          let srv = this;

          if (siteSettings.useHLSOffloading) {
            srv.selectUserLocation().then(() => {
                srv.configurePlayerEvents();
                srv.toggleVideoPlayer();
            });
          } else {
            srv.configurePlayerEvents();
            srv.toggleVideoPlayer();
          }
        }
    }

    checkIfUserIsAnonymous() {
        if (
            this.user !== null &&
            !userPerms.aadAuthenticated(this.user) &&
            this.userInteractionService
        ) {
            this.userInteractionService.displayName = utils.getUserDisplayNameWithCompany(
                this.user
            );
            this.userInteractionService.userEmail = this.user._au;
            this.userInteractionService.setDisplayname();
        }
    }

    finalizeChat() {
        let ctrl = this;

        if (siteSettings.isEmbeddedPage || this.event.userInteractionType === 0) {
            return;
        }

        (function loader(counter) {
            utils.log('watch', 'Checking userInteractionResponsive #' + counter, true, 1);
            if (counter > 10) {
                utils.log('watch', 'Chat not set to ready state. Aborting wait loop.', true, 2);
                return;
            }
            var timer = setTimeout(() => {
                if (ctrl.userInteractionResponsive) {
                    ctrl.checkIfUserIsAnonymous();
                } else {
                    if (window.networkError) {
                        clearTimeout(timer);
                    } else {
                        loader(counter + 1);
                    }
                }
            }, 1000);
        })(0);
    }

    // Logic for trying different failover stream sources based on configuration
    failoverVideoSource() {
        if (this.event.useWowzaVideo)
          return;
        else if (this.useOnPremStreaming) {
            this.useOnPremStreaming = false;
            this.customStreamSource = 'cloud';
        } else if (!this.useCloudBackupUrl && this.cloudStreamBackupUrl !== '') {
            this.useCloudBackupUrl = true;
        } else if (this.networkInfo && !this.networkInfo.useCloudStreamSource) {
            this.useOnPremStreaming = true;
            this.useCloudBackupUrl = false;
            this.customStreamSource = null;
        } else {
            this.useCloudBackupUrl = false;
        }
    }

    tryAutoplayVideo(player) {
        let forceAutoplay = window.location.search.match(/(&|\?)autoplay(&|$)/i);

        if (
            forceAutoplay ||
            this.videoSwitchedFromCountdown ||
            (!siteSettings.isEmbeddedPage && utils.isLocallyReferred())
        ) {
            utils.log('videoplayer', 'Trying to autoplay video', true, 1);

            var apPromise = player.play();

            if (apPromise !== undefined) {
                apPromise
                    .then(function () {
                        // Autoplay started, nothing more to do...
                    })
                    .catch(function () {
                        // let's mute the player and try again..
                        player.muted(true);
                        player.play();
                    });
            }
        } else {
            utils.log(
                'videoplayer',
                'In embed mode or not locally referred - no autoplay',
                true,
                1
            );
        }
    }

    /**
     * Hides and shows the video player based on error state, and shows the appropriate image
     */
    toggleVideoPlayer() {
        var vjsPlayer = utils.getVJSPlayer(videoJS);
        const vjsElem = document.getElementsByClassName('video-js');
        const vjsPlaceholder = document.getElementsByClassName('video-js-placeholder');
        let bgImgUrl = '';

        const hasEventEnded = this.event.postEventBehavior === 0 && utils.hasEventGracePeriodEnded(this.event);
        const waitingForWV = this.event.useWowzaVideo && !this.event.wvStreamIsLive;

        if (!this.isVideoPublished() && (hasEventEnded || waitingForWV)) {
            utils.log('videoplayer', 'The webcast has ended or there is no stream - showing waiting picture', true, 1);

            if (this.videoPlayerState === 'playing')
                vjsPlayer.pause();

            setTimeout(() => {
              bgImgUrl = siteSettings.waitingEmbedPictureUrl;
              vjsElem[0].style.setProperty('display', 'none');
              vjsPlaceholder[0].style.setProperty('display', 'block');
              vjsPlaceholder[0].style.setProperty('background-image', 'url(' + bgImgUrl + ')');
            }, 0);
        } else {
            if (this.videoPlayerState === 'error') {
              utils.log(
                'videoplayer',
                'The live stream stopped because the encoder/studio stopped streaming',
                true,
                1
              );
            } else {
              utils.log('videoplayer', 'No error - showing video player', true, 1);
            }

            setTimeout(() => {
              vjsPlayer.poster(this.event.mediaUrls['VideoPoster']);
              vjsElem[0].style.setProperty('display', 'block');
              vjsPlaceholder[0].style.setProperty('display', 'none');
              vjsPlaceholder[0].style.removeProperty('background-image');
            }, 0);
            let streamUrl = this.getPlaylistUrl();

            let wseUrlMatch = false;
            if (!this.event.useWowzaVideo && vjsPlayer.src()) {
              let streamUrlWithoutQuery = new URL(streamUrl);
              streamUrlWithoutQuery.search = '';
              let vjsSrcWithoutQuery = new URL(vjsPlayer.src());
              vjsSrcWithoutQuery.search = '';
              wseUrlMatch = streamUrlWithoutQuery.toString() === vjsSrcWithoutQuery.toString();
            }

            if ((!this.event.useWowzaVideo && !wseUrlMatch) ||
              (this.event.useWowzaVideo && (vjsPlayer.src() !== streamUrl || this.videoPlayerState === 'waiting')) ||
              this.videoPlayerState === 'error') {
              utils.log('videoplayer', 'Setting video player source to ' + streamUrl, true, 1);
              vjsPlayer.src({ type: 'application/x-mpegURL', src: streamUrl });

              // Skip to a specific VOD start time
              if (this.isVideoPublished()) {
                  const userSavedTimeStamp = localStorage.getItem(utils.vodCurrentTimeKey(this.event));
                  if (this.event.useWowzaVideo && this.event.voDPlayStart) {
                    if (userSavedTimeStamp && userSavedTimeStamp !== '' && userSavedTimeStamp > this.event.voDPlayStart) {
                      vjsPlayer.currentTime(parseFloat(userSavedTimeStamp));
                    } else {
                      vjsPlayer.currentTime(parseFloat(this.event.voDPlayStart));
                    }
                  } else if (userSavedTimeStamp && userSavedTimeStamp !== '') {
                    vjsPlayer.currentTime(parseFloat(userSavedTimeStamp));
                  }
              }

              this.tryAutoplayVideo(vjsPlayer);
            }
        }
    }

    /**
     * Create announcement overlay if it is not in place when player is in fullscreen mode
     */
    ensureAnnouncementOverlayElement() {
        if (!document.getElementById('vl-announcement-overlay')) {
            let outerDiv = document.createElement('div');
            outerDiv.id = 'vl-announcement-overlay';
            outerDiv.className = 'vl-announce-overlay';
            let messageContainerDiv = document.createElement('div');
            messageContainerDiv.id = 'vl-announcement-mc';
            messageContainerDiv.className = 'vl-announce-mc';
            let unDiv = document.createElement('div');
            unDiv.id = 'vl-announcement-un';
            unDiv.className = 'vl-announce-un';
            let messDiv = document.createElement('div');
            messDiv.id = 'vl-announcement-mess';
            messDiv.className = 'vl-announce-mess';
            messageContainerDiv.appendChild(unDiv);
            messageContainerDiv.appendChild(messDiv);
            outerDiv.appendChild(messageContainerDiv);
            document.getElementById('vjs-player').appendChild(outerDiv);
        }
    }

    /**
     * Completely remove element when exiting fullscreen
     */
    removeAnnouncementElem() {
        var element = document.getElementById('vl-announcement-overlay');
        if (element) element.parentElement.removeChild(element);
    }

    /**
     * Show announcement in player if player is in fullscreen mode
     */
    showFullscreenAnnouncement(fromUser, message) {
        var vjsPlayer = utils.getVJSPlayer(videoJS);
        if (vjsPlayer.isFullscreen()) {
            document.getElementById('vl-announcement-overlay').className += ' show';
            document.getElementById('vl-announcement-un').innerHTML = fromUser;
            document.getElementById('vl-announcement-mess').innerHTML = message;
            window.setTimeout(this.hidePlayerAnnouncement, 12000);
        }
    }

    /**
     * Make the announcement disappear...
     */
    hidePlayerAnnouncement() {
        var element = document.getElementById('vl-announcement-overlay');
        if (element) element.className = element.className.replace(/\bshow\b/g, '');
    }

    canUserComment() {
        return (
            userPerms.aadAuthenticated(this.user) ||
            userPerms.anonAuthenticated(this.event.accessKey, this.user)
        );
    }
}

WatchService.$inject = ['$rootScope', '$log', '$interval', 'uiService'];
